import { useEffect, useRef, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { clsx } from 'clsx'
import { ArrowUpRight, Circle, CircleCheck } from 'lucide-react'

import DimensionIcon from './DimensionIcon'
import { dimensionLabels, dimensionValueLabel, formatCount, formatDuration, formatPercent, formatValue } from './model'
import { ErrorState, LoadingState, Ratio, StaleNotice } from './shared'
import {
  isTransientQueryError,
  useAttributionCohorts,
  useInfiniteMeetingPreview,
  useMeetingSpeakerLabelMeetings,
} from './useAttributionObservability'

import styles from './AttributionObservabilityPage.module.scss'

import {
  type AttributionDimension,
  type AttributionFilters,
  type AttributionSourceSelection,
  type AttributionSummary,
  facetDimensions,
  type MeetingSpeakerLabelMeeting,
} from '@/api/attributionObservability'
import Button from '@/components/Button'
import DataTable from '@/components/DataTable'
import Select from '@/components/Select'
import { viewerHref } from '@/pages/app/AttributionViewer/mode'
import { compactAmPm } from '@/utils/compactAmPm'

const VersionComparison = ({
  filters,
  captureCoverage = false,
}: {
  filters: AttributionFilters
  captureCoverage?: boolean
}) => {
  const [dimension, setDimension] = useState<AttributionDimension>('backend_version')
  const query = useAttributionCohorts(filters, dimension)
  const columns: ColumnDef<{ value: string; summary: AttributionSummary }>[] = [
    { accessorKey: 'value', header: dimensionLabels[dimension], cell: ({ row }) => formatValue(row.original.value) },
    { id: 'meetings', header: 'Meetings', accessorFn: (row) => row.summary.meeting_count },
    {
      id: 'machine',
      header: 'Machine labels',
      cell: ({ row }) => (
        <Ratio
          numerator={row.original.summary.machine_labeled_speakers}
          denominator={row.original.summary.active_speakers}
        />
      ),
    },
    {
      id: 'machineWords',
      header: 'Machine labels (words)',
      cell: ({ row }) => (
        <Ratio
          numerator={row.original.summary.machine_labeled_words}
          denominator={row.original.summary.words}
          unit="words"
        />
      ),
    },
    {
      id: 'ax',
      header: captureCoverage ? 'AX capture' : 'AX evidence',
      cell: ({ row }) => (
        <Ratio
          numerator={captureCoverage ? row.original.summary.ax_readable_ms : row.original.summary.ax_covered_speech_ms}
          denominator={captureCoverage ? row.original.summary.ax_capture_ms : row.original.summary.speech_ms}
          unit="duration"
        />
      ),
    },
    {
      id: 'unmeasured',
      header: 'Without label measurement',
      accessorFn: (row) => row.summary.meeting_count - row.summary.measured_label_meetings,
    },
  ]
  return (
    <section className={styles.panel}>
      <div className={styles.sectionHeader}>
        <div>
          <h2>Compare versions</h2>
          <p className={styles.muted}>Same filters, grouped by the selected version</p>
        </div>
        <div className={styles.field}>
          <label htmlFor="version-comparison">Compare by</label>
          <Select
            id="version-comparison"
            value={dimension}
            onChange={(value) => setDimension(value as AttributionDimension)}
            options={facetDimensions.map((value) => ({
              value,
              label: dimensionLabels[value],
            }))}
          />
        </div>
      </div>
      {query.data?.truncated && (
        <p className={styles.muted}>Showing the 100 largest groups. Add a filter to narrow results.</p>
      )}
      {query.isError && query.data && isTransientQueryError(query.error) && (
        <StaleNotice onRetry={() => void query.refetch()} />
      )}
      {query.isError && (!query.data || !isTransientQueryError(query.error)) ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          data={query.data?.groups ?? []}
          columns={columns}
          isLoading={query.isPending}
          ariaLabel="Coverage by version"
          emptyMessage="No version measurements match these filters."
        />
      )}
    </section>
  )
}

const RunDetails = ({ run, compact = false }: { run: MeetingSpeakerLabelMeeting; compact?: boolean }) => (
  <details className={styles.runDetails}>
    <summary>{compact ? 'Metadata' : 'Inspect metadata'}</summary>
    <dl>
      <dt>Meeting key</dt>
      <dd>{run.meeting_key}</dd>
      <dt>Client</dt>
      <dd>{formatValue(run.client)}</dd>
      <dt>App version</dt>
      <dd>{dimensionValueLabel('app_version', run.app_version ?? 'unknown')}</dd>
      <dt>Calendar event</dt>
      <dd>{dimensionValueLabel('calendar_event', run.calendar_event)}</dd>
      <dt>User meeting number</dt>
      <dd>{run.user_meeting_number ?? 'Unknown'}</dd>
      <dt>Recorded duration</dt>
      <dd>{run.recorded_ms === null ? 'Unknown' : formatDuration(run.recorded_ms)}</dd>
      <dt>Detected speakers / calendar attendees</dt>
      <dd>
        {run.active_speakers ?? 'Unknown'} / {run.invited_attendee_count ?? 'Unknown'}
      </dd>
      <dt>Backend version</dt>
      <dd>{run.backend_version ?? 'Unknown'}</dd>
      <dt>Label measurement</dt>
      <dd>{formatValue(run.label_measurement_status)}</dd>
      <dt>AX evidence measurement</dt>
      <dd>{formatValue(run.ax_measurement_status)}</dd>
      <dt>AX capture measurement</dt>
      <dd>{formatValue(run.ax_capture_status)}</dd>
      <dt>Summary completed</dt>
      <dd>{compactAmPm(new Date(run.completed_at).toLocaleString())}</dd>
    </dl>
  </details>
)

const RunRatio = ({
  numerator,
  denominator,
  status,
  unit = 'speakers',
  compact,
}: {
  numerator: number | null
  denominator: number | null
  status: string
  unit?: 'speakers' | 'duration'
  compact: boolean
}) => {
  if (!compact) return <Ratio numerator={numerator} denominator={denominator} status={status} unit={unit} />
  const value =
    status === 'not_applicable'
      ? 'Not applicable'
      : status === 'unknown'
        ? 'Not measured'
        : status !== 'measured'
          ? formatValue(status)
          : formatPercent(numerator, denominator)
  const detail =
    numerator !== null && denominator !== null && status === 'measured'
      ? unit === 'duration'
        ? `${formatDuration(numerator)} / ${formatDuration(denominator)}`
        : `${formatCount(numerator)} / ${formatCount(denominator)} speakers`
      : undefined
  return (
    <span className={styles.inlineRatio} title={detail}>
      {value}
      {detail && <span className={styles.srOnly}> ({detail})</span>}
    </span>
  )
}

type MeetingInspection = { label: string; onClose: () => void }

const MeetingList = ({
  filters,
  sourceSelection,
  inspection,
  variant = 'standard',
  captureCoverage = false,
}: {
  filters: AttributionFilters
  sourceSelection?: AttributionSourceSelection
  inspection?: MeetingInspection
  variant?: 'standard' | 'inline'
  captureCoverage?: boolean
}) => {
  const compact = variant === 'inline'
  const heading = useRef<HTMLHeadingElement>(null)
  const hasInspection = Boolean(inspection)
  useEffect(() => {
    if (!hasInspection) return
    heading.current?.focus({ preventScroll: true })
    heading.current?.parentElement?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  }, [hasInspection])
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const pageQuery = useMeetingSpeakerLabelMeetings(
    { ...filters, ...sourceSelection },
    cursors[cursors.length - 1],
    !compact
  )
  const preview = useInfiniteMeetingPreview({ ...filters, ...sourceSelection }, compact)
  const query = compact ? preview : pageQuery
  const items = compact ? (preview.data?.pages.flatMap((page) => page.items) ?? []) : (pageQuery.data?.items ?? [])
  const columns = (
    [
      {
        accessorKey: 'meeting_created_at',
        header: 'Created (UTC)',
        cell: ({ row }) => new Date(row.original.meeting_created_at).toISOString().replace('T', ' ').slice(0, 16),
        enableSorting: false,
      },
      {
        id: 'key',
        header: 'Meeting',
        cell: ({ row }) => (
          <span className={styles.meetingKey} title={row.original.meeting_key}>
            {row.original.meeting_key.slice(0, 12)}
          </span>
        ),
      },
      {
        accessorKey: 'platform',
        header: 'Platform',
        cell: ({ row }) => (
          <span className={clsx(styles.platformCell, compact && styles.compactPlatformCell)}>
            <span className={styles.iconLabel}>
              <DimensionIcon dimension="platform" value={row.original.platform ?? 'unknown'} size={14} />
              {formatValue(row.original.platform)}
            </span>
            {compact && <span aria-hidden="true">·</span>}
            <span className={clsx(styles.iconLabel, !compact && styles.osLabel)}>
              <DimensionIcon dimension="os" value={row.original.os ?? 'unknown'} size={compact ? 14 : 12} />
              {formatValue(row.original.os)}
            </span>
          </span>
        ),
      },
      {
        id: 'machine',
        header: compact ? 'Labels' : 'Machine labels',
        cell: ({ row }) => (
          <RunRatio
            compact={compact}
            numerator={row.original.machine_labeled_speakers}
            denominator={row.original.active_speakers}
            status={row.original.label_measurement_status}
          />
        ),
      },
      {
        id: 'ax',
        header: captureCoverage ? 'AX capture' : 'AX evidence',
        cell: ({ row }) => (
          <RunRatio
            compact={compact}
            numerator={captureCoverage ? row.original.ax_readable_ms : row.original.ax_covered_speech_ms}
            denominator={captureCoverage ? row.original.ax_capture_ms : row.original.speech_ms}
            unit="duration"
            status={captureCoverage ? row.original.ax_capture_status : row.original.ax_measurement_status}
          />
        ),
      },
      {
        id: 'viewer',
        header: compact ? 'Viewer' : 'Attribution viewer',
        cell: ({ row }) =>
          row.original.viewer_meeting_id ? (
            <a
              className={styles.viewerLink}
              href={`/app/attribution/${encodeURIComponent(row.original.viewer_meeting_id)}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open viewer for meeting ${row.original.meeting_key.slice(0, 12)}`}
            >
              Open viewer <ArrowUpRight size={13} aria-hidden="true" />
            </a>
          ) : (
            <span className={styles.muted}>Viewer unavailable</span>
          ),
      },
      {
        id: 'annotator',
        header: 'Annotator',
        cell: ({ row }) => (
          <span className={styles.annotator}>
            {row.original.annotated === true ? (
              <CircleCheck size={13} className={styles.annotatedIcon} role="img" aria-label="Annotated" />
            ) : row.original.annotated === false ? (
              <Circle size={13} className={styles.muted} role="img" aria-label="Not annotated" />
            ) : null}
            {row.original.viewer_meeting_id ? (
              <a
                className={styles.viewerLink}
                href={viewerHref(row.original.viewer_meeting_id, 'annotate')}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Annotate meeting ${row.original.meeting_key.slice(0, 12)}`}
              >
                Annotate <ArrowUpRight size={13} aria-hidden="true" />
              </a>
            ) : (
              <span className={styles.muted}>Unavailable</span>
            )}
          </span>
        ),
      },
      { id: 'details', header: 'Details', cell: ({ row }) => <RunDetails run={row.original} compact={compact} /> },
    ] satisfies ColumnDef<MeetingSpeakerLabelMeeting>[]
  )
    .filter((column) => !compact || column.id !== 'key')
    .map((column) => ({ ...column, enableSorting: false }))
  return (
    <section
      className={compact ? styles.inlineMeetings : styles.panel}
      id={inspection ? 'cohort-meetings' : undefined}
      aria-label={inspection ? `Meetings: ${inspection.label}` : undefined}
    >
      {compact ? (
        <h2 ref={heading} tabIndex={-1} className={styles.srOnly}>
          Meetings: {inspection?.label ?? 'All meetings'}
        </h2>
      ) : (
        <>
          <div className={styles.sectionHeader}>
            <h2 className={styles.meetingHeading}>Meeting metadata</h2>
            <span className={styles.muted}>Latest result · newest first</span>
          </div>
          <p className={styles.viewerExplanation}>
            Viewer access requires an available source and either ownership, model-improvement sharing, or a report.
          </p>
        </>
      )}
      {query.isError && query.data && isTransientQueryError(query.error) && (
        <StaleNotice onRetry={() => void query.refetch()} />
      )}
      {query.isError && (!query.data || !isTransientQueryError(query.error)) ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : (
        <div
          className={compact ? styles.previewScroll : undefined}
          role={compact ? 'region' : undefined}
          aria-label={compact ? 'Scrollable meeting preview' : undefined}
          tabIndex={compact ? 0 : undefined}
          onScroll={
            compact
              ? (event) => {
                  const element = event.currentTarget
                  if (
                    element.scrollHeight - element.scrollTop - element.clientHeight < 120 &&
                    preview.hasNextPage &&
                    !preview.isFetching &&
                    !preview.isFetchNextPageError
                  ) {
                    void preview.fetchNextPage()
                  }
                }
              : undefined
          }
        >
          {query.isPending ? (
            <LoadingState label="Loading meetings…" skeleton />
          ) : (
            <DataTable
              data={items}
              columns={columns}
              isLoading={query.isPending}
              manualPagination
              manualSorting
              ariaLabel={inspection ? `Meetings: ${inspection.label}` : 'Meeting metadata'}
              emptyMessage="No meetings match these filters."
            />
          )}
          {compact && (preview.hasNextPage || preview.isFetchingNextPage || preview.isFetchNextPageError) && (
            <div className={styles.previewStatus}>
              {preview.isFetchingNextPage ? (
                <LoadingState label="Loading more meetings…" />
              ) : preview.hasNextPage ? (
                'Scroll for more meetings'
              ) : null}
              {preview.isFetchNextPageError && (
                <button type="button" onClick={() => void preview.fetchNextPage()}>
                  Retry loading more
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {!compact && (
        <div className={styles.pagination}>
          <span>Page {cursors.length}</span>
          <Button
            title="Previous"
            type="light"
            isDisabled={cursors.length === 1 || query.isFetching}
            onClick={() => setCursors(cursors.slice(0, -1))}
          />
          <Button
            title="Next"
            type="light"
            isDisabled={!pageQuery.data?.next_cursor || query.isFetching}
            onClick={() => {
              if (pageQuery.data?.next_cursor) setCursors([...cursors, pageQuery.data.next_cursor])
            }}
          />
        </div>
      )}
    </section>
  )
}

export { MeetingList, VersionComparison }
